import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { TimerangePicker } from 'components/inputs/TimerangePicker';
import { TimerangePickerProps } from 'components/inputs/TimerangePicker/TimerangePicker.types';
import UnitSelector from 'components/inputs/UnitSelector/UnitSelector';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { GreenerUnit } from 'types';

const STICKY_TOP_OFFSET = -24; // cancels MainLayout's mobile pt(16) + Grid's own pt(8) so the topbar is sticked to the top
const STICKY_WIDTH_OFFSET = 32; // cancels MainLayout's mobile px(16) on each side so scrolled content is hidden behind the topbar

export function UnitDetailTopbar({
  unitSelectorProps,
  timerangePickerProps,
  showRangePicker = true,
  title,
  rightSlot,
  leftSlot,
  isSticky = false,
}: {
  unitSelectorProps: {
    units: GreenerUnit[];
    selectedUnitExtId?: string;
    onChange?: (unit: GreenerUnit) => void;
    mode?: 'single' | 'multi';
    selectedUnitExtIds?: string[];
    onMultiChange?: (units: GreenerUnit[]) => void;
    showSelectAll?: boolean;
  };
  timerangePickerProps: TimerangePickerProps;
  showRangePicker?: boolean;
  title?: string;
  rightSlot?: ReactNode;
  leftSlot?: ReactNode;
  isSticky?: boolean;
}) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  return (
    <Grid
      container
      spacing={2}
      pt={1}
      pb={2}
      sx={
        isSticky
          ? {
              position: 'sticky',
              top: STICKY_TOP_OFFSET,
              zIndex: (theme) => theme.zIndex.appBar - 1,
              backgroundColor: (theme) => theme.palette.background.default,
              width: `calc(100% + ${STICKY_WIDTH_OFFSET}px)`,
              pr: 2,
            }
          : undefined
      }
    >
      {(title || showRangePicker) && (
        <Grid container item xs={12} justifyContent="space-between" alignItems="center" mb={isMobile || !title ? 0 : 2}>
          {title && (
            <Grid item mb={isMobile ? 4 : 0}>
              <Typography variant="h1">{title}</Typography>
            </Grid>
          )}
          {showRangePicker && (
            <Grid item xs={12} sm={6} md={5} sx={{ ml: 'auto' }}>
              <TimerangePicker {...timerangePickerProps} />
            </Grid>
          )}
        </Grid>
      )}
      <Grid container item xs={12} spacing={2} justifyContent="space-between" alignItems="center">
        {leftSlot && <Grid item>{leftSlot}</Grid>}
        <Grid item xs={leftSlot ? true : 12} md={5} xl={4}>
          <UnitSelector
            units={unitSelectorProps.units}
            selectedUnitExtId={unitSelectorProps.selectedUnitExtId}
            handleChange={unitSelectorProps.onChange}
            fieldLabel={t('entities:greenerUnit.nameSingular')}
            mode={unitSelectorProps.mode}
            selectedUnitExtIds={unitSelectorProps.selectedUnitExtIds}
            onMultiChange={unitSelectorProps.onMultiChange}
            showSelectAll={unitSelectorProps.showSelectAll}
            size={isMobile ? 'medium' : 'small'}
          />
        </Grid>
        {rightSlot && (
          <Grid item xs={12} md={7} xl={5}>
            {rightSlot}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
